import { useEffect, useState, useRef, useCallback } from 'react'

export interface UseTimerOutput {
  time: number
  remainingTime: number
  isRunning: boolean
  start: (ignoreRunning?: boolean) => void
  stop: () => void
}

const useTimer = (seconds: number): UseTimerOutput => {
  const [time, setTime] = useState(0)
  const [isRunning, setIsRunning] = useState(false)
  const intervalRef = useRef<number | null>(null)

  const _clearInterval = useCallback(() => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current)
    }
  }, [])

  const start = useCallback(
    (ignoreRunning = false) => {
      if (isRunning && !ignoreRunning) return

      setIsRunning(true)
      setTime(0)

      intervalRef.current = window.setInterval(() => {
        setTime((prev) => {
          if (prev >= seconds - 1) {
            setIsRunning(false)
            _clearInterval()
            return seconds
          }
          return prev + 1
        })
      }, 1000)
    },
    [isRunning, seconds, _clearInterval]
  )

  const stop = useCallback(() => {
    setIsRunning(false)
    _clearInterval()
  }, [_clearInterval])

  useEffect(() => {
    return () => _clearInterval()
  }, [])

  return {
    time,
    remainingTime: Math.max(seconds - time, 0),
    isRunning,
    start,
    stop,
  }
}

export default useTimer
